import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { Globe, ChevronDown, LogOut, ShieldCheck, User, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function Logo() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
      <img
        src="/brand/plastonord-logo.png"
        alt="PlastoNord GmbH"
        className="h-10 w-auto md:h-11"
        draggable="false"
      />
      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4A4D52] sm:inline">
        {t('brand.portal')}
      </span>
    </Link>
  );
}

function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const setLang = (l) => i18n.changeLanguage(l);
  const current = (i18n.resolvedLanguage || i18n.language || 'de').toUpperCase().slice(0, 2);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="header-language-switcher-trigger"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F0] bg-white px-3 py-1.5 text-sm font-medium text-[#14171C] transition hover:border-[#14171C]/25"
        >
          <Globe className="h-4 w-4 text-[#4A4D52]" />
          <span className="font-mono">{current}</span>
          <ChevronDown className="h-3.5 w-3.5 text-[#4A4D52]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        <DropdownMenuItem onClick={() => setLang('de')} data-testid="header-language-switcher-de">
          <span className="font-mono mr-2">DE</span> {t('lang.de')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLang('en')} data-testid="header-language-switcher-en">
          <span className="font-mono mr-2">EN</span> {t('lang.en')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const navItems = [
    { to: '/catalog', label: t('nav.catalog') },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-[#E4E9F0] bg-white/90 backdrop-blur">
      <div className="pn-container flex h-16 items-center justify-between">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-6 md:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`header-nav-${n.to.replace('/', '')}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition ${isActive ? 'text-[#E2611C]' : 'text-[#14171C] hover:text-[#E2611C]'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user && (
              <NavLink to="/portal" data-testid="header-nav-portal" className={({ isActive }) => `text-sm font-medium transition ${isActive ? 'text-[#E2611C]' : 'text-[#14171C] hover:text-[#E2611C]'}`}>
                {t('nav.portal')}
              </NavLink>
            )}
            {user?.role === 'admin' && (
              <NavLink to="/admin" data-testid="header-nav-admin" className={({ isActive }) => `inline-flex items-center gap-1.5 text-sm font-medium transition ${isActive ? 'text-[#E2611C]' : 'text-[#14171C] hover:text-[#E2611C]'}`}>
                <ShieldCheck className="h-4 w-4" />{t('nav.admin')}
              </NavLink>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {!user ? (
            <Link to="/login" data-testid="header-login-button" className="hidden md:inline-flex pn-btn-dark !py-2 !px-4">
              {t('nav.login')}
            </Link>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-user-menu" className="inline-flex items-center gap-2 rounded-full border border-[#E4E9F0] bg-white px-3 py-1.5 text-sm font-medium text-[#14171C]">
                  <User className="h-4 w-4 text-[#4A4D52]" />
                  <span className="max-w-[140px] truncate">{user.full_name || user.email}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-[#4A4D52]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuItem onClick={() => navigate('/portal')} data-testid="user-menu-portal">
                  {t('nav.dashboard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/portal/requests')} data-testid="user-menu-requests">
                  {t('nav.my_requests')}
                </DropdownMenuItem>
                {user.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="user-menu-admin">
                    {t('nav.admin')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => { logout(); navigate('/'); }}
                  data-testid="user-menu-logout"
                  className="text-[#B42318]"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            data-testid="header-mobile-toggle"
            aria-label="Menu"
            className="inline-flex items-center justify-center rounded-full border border-[#E4E9F0] bg-white p-2 md:hidden"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#E4E9F0] bg-white">
          <div className="pn-container flex flex-col gap-2 py-4">
            <Link to="/catalog" onClick={() => setOpen(false)} className="py-2 text-sm font-medium">{t('nav.catalog')}</Link>
            {user && <Link to="/portal" onClick={() => setOpen(false)} className="py-2 text-sm font-medium">{t('nav.portal')}</Link>}
            {user?.role === 'admin' && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-sm font-medium">{t('nav.admin')}</Link>}
            {!user ? (
              <Link to="/login" onClick={() => setOpen(false)} className="pn-btn-dark !py-2 !px-4 mt-2 justify-center">{t('nav.login')}</Link>
            ) : (
              <button onClick={() => { logout(); setOpen(false); navigate('/'); }} className="pn-btn-outline !py-2 !px-4 mt-2 justify-center">
                <LogOut className="h-4 w-4" /> {t('nav.logout')}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
